'use client'

import Header from '@/components/header'
import {
  ArrowLeft,
  FileText,
  Loader2,
  Download,
  Copy,
  Check,
  Sparkles,
  AlertCircle,
  Upload,
} from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'

export default function Page() {
  const router = useRouter()
  const [wordUrl, setWordUrl] = useState('')
  const [extractedText, setExtractedText] = useState('')
  const [isExtracting, setIsExtracting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 提取Word文档文本
  const handleExtractText = async () => {
    if (!wordUrl.trim()) {
      setError('请输入Word文档URL')
      return
    }

    setIsExtracting(true)
    setError(null)
    setExtractedText('')

    try {
      // 获取Word文档
      const response = await fetch(wordUrl)
      if (!response.ok) {
        throw new Error('无法获取Word文档，请检查URL是否正确')
      }

      const blob = await response.blob()

      // 检查文件类型
      const fileName = wordUrl.split('/').pop()?.toLowerCase() || ''
      const isDocx = fileName.endsWith('.docx') || blob.type.includes('wordprocessingml')

      if (!isDocx && !fileName.endsWith('.doc')) {
        throw new Error('URL指向的不是Word文档格式')
      }

      if (isDocx) {
        // 提取 docx 文件内容
        const text = await extractDocxText(blob)
        setExtractedText(text)
      } else {
        throw new Error('暂不支持 .doc 格式，请使用 .docx 格式')
      }
    } catch (err: any) {
      console.error('提取文本失败:', err)
      setError(err.message || '提取文本失败，请稍后重试')
    } finally {
      setIsExtracting(false)
    }
  }

  // 从 docx 文件中提取文本
  const extractDocxText = async (blob: Blob): Promise<string> => {
    try {
      // 动态导入 jszip
      const JSZip = (await import('jszip')).default

      const zip = await JSZip.loadAsync(blob)

      // 读取 document.xml 文件
      const documentXml = await zip.file('word/document.xml')?.async('string')
      if (!documentXml) {
        throw new Error('无法读取Word文档内容')
      }

      // 解析XML并提取文本
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(documentXml, 'text/xml')

      // 获取所有文本节点
      const textNodes = xmlDoc.getElementsByTagName('w:t')
      let text = ''

      for (let i = 0; i < textNodes.length; i++) {
        text += textNodes[i].textContent + ''
      }

      // 处理段落换行
      const paragraphs = xmlDoc.getElementsByTagName('w:p')
      let formattedText = ''
      for (let i = 0; i < paragraphs.length; i++) {
        const pTextNodes = paragraphs[i].getElementsByTagName('w:t')
        let pText = ''
        for (let j = 0; j < pTextNodes.length; j++) {
          pText += pTextNodes[j].textContent
        }
        if (pText.trim()) {
          formattedText += pText + '\n\n'
        }
      }

      return formattedText.trim() || text.trim() || '文档中没有提取到文本内容'
    } catch (error) {
      console.error('解析docx文件失败:', error)
      throw new Error('解析Word文档失败')
    }
  }

  // 复制文本到剪贴板
  const handleCopy = async () => {
    if (!extractedText) return

    try {
      await navigator.clipboard.writeText(extractedText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('复制失败:', err)
    }
  }

  // 下载为文本文件
  const handleDownload = () => {
    if (!extractedText) return

    const blob = new Blob([extractedText], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `extracted-text-${Date.now()}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Enter键提交
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isExtracting) {
      handleExtractText()
    }
  }

  // 处理文件上传
  const handleFileUpload = async (file: File) => {
    setError(null)
    setExtractedText('')
    setUploadedFileName(file.name)

    // 检查文件类型
    const fileName = file.name.toLowerCase()
    const isDocx = fileName.endsWith('.docx')

    if (!isDocx && !fileName.endsWith('.doc')) {
      setError('请上传 .docx 或 .doc 格式的Word文档')
      setUploadedFileName(null)
      return
    }

    if (!isDocx) {
      setError('暂不支持 .doc 格式，请使用 .docx 格式')
      setUploadedFileName(null)
      return
    }

    setIsExtracting(true)

    try {
      const text = await extractDocxText(file)
      setExtractedText(text)
    } catch (err: any) {
      console.error('提取文本失败:', err)
      setError(err.message || '提取文本失败，请稍后重试')
      setUploadedFileName(null)
    } finally {
      setIsExtracting(false)
    }
  }

  // 文件选择
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileUpload(file)
    }
  }

  // 拖拽事件
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = () => {
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)

    const file = e.dataTransfer.files[0]
    if (file) {
      handleFileUpload(file)
    }
  }

  return (
    <>
      <Header />

      <main className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 relative overflow-hidden">
        {/* 背景装饰 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse-slow" />
          <div
            className="absolute bottom-20 right-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl animate-pulse-slow"
            style={{ animationDelay: '2s' }}
          />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-primary/3 to-secondary/3 rounded-full blur-3xl animate-spin-slow" />
        </div>

        {/* 内容区域 */}
        <div className="relative max-w-5xl mx-auto px-4 py-8">
          {/* 返回按钮 */}
          <button
            onClick={() => router.push('/light')}
            className="group mb-8 flex items-center gap-2 px-4 py-3 rounded-2xl bg-card/80 backdrop-blur-xl border-2 border-border hover:border-primary shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-primary/20 rounded-full blur-md scale-0 group-hover:scale-150 transition-transform duration-500" />
              <ArrowLeft className="relative w-5 h-5 text-primary group-hover:text-primary transition-all duration-300 group-hover:-translate-x-1" />
            </div>
            <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors duration-300">
              返回
            </span>
          </button>

          {/* 主标题卡片 */}
          <div className="mb-8 p-8 rounded-3xl bg-gradient-to-br from-card/95 to-card/80 backdrop-blur-2xl border-2 border-border/50 shadow-2xl animate-fade-in">
            <div className="flex items-center gap-4 mb-3">
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 rounded-2xl blur-xl animate-pulse-slow" />
                <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg">
                  <FileText className="w-8 h-8 text-primary-foreground" />
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  Word文本提取器
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  输入Word文档URL或上传文件，一键提取文字内容
                </p>
              </div>
            </div>
          </div>

          {/* 输入区域 */}
          <div className="mb-8 p-6 rounded-3xl bg-gradient-to-br from-card/95 to-card/80 backdrop-blur-2xl border-2 border-primary/30 shadow-2xl hover:shadow-primary/20 transition-all duration-300 animate-fade-in">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-primary animate-pulse" />
              <label className="text-sm font-semibold text-foreground">
                Word文档URL
              </label>
            </div>

            <div className="flex gap-3">
              <Input
                type="url"
                placeholder="https://example.com/document.docx"
                value={wordUrl}
                onChange={(e) => setWordUrl(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isExtracting}
                className="flex-1 h-12 px-4 bg-background/50 border-2 border-border focus:border-primary rounded-xl text-base shadow-inner transition-all duration-300"
              />
              <Button
                onClick={handleExtractText}
                disabled={isExtracting || !wordUrl.trim()}
                className="h-12 px-6 rounded-xl bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExtracting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    提取中...
                  </>
                ) : (
                  <>
                    <FileText className="w-5 h-5 mr-2" />
                    提取文本
                  </>
                )}
              </Button>
            </div>

            {/* 提示信息 */}
            <div className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>支持 .docx 格式的Word文档，URL需要可以直接访问</span>
            </div>
          </div>

          {/* 分隔线 */}
          <div className="mb-8 flex items-center gap-4">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
            <span className="text-sm font-medium text-muted-foreground">或</span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
          </div>

          {/* 文件上传区域 */}
          <div className="mb-8 p-6 rounded-3xl bg-gradient-to-br from-card/95 to-card/80 backdrop-blur-2xl border-2 border-border/50 shadow-2xl hover:shadow-primary/10 transition-all duration-300 animate-fade-in">
            <div className="flex items-center gap-2 mb-3">
              <Upload className="w-5 h-5 text-primary animate-pulse" />
              <label className="text-sm font-semibold text-foreground">
                上传Word文档
              </label>
            </div>

            <div
              className={`
                border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer
                transition-all duration-300 relative overflow-hidden
                ${
                  isDragOver
                    ? 'border-primary bg-primary/10 scale-[1.02]'
                    : 'border-border hover:border-primary/50 hover:bg-muted/30'
                }
              `}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {/* 装饰性背景 */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-0 left-1/4 w-32 h-32 bg-primary/5 rounded-full blur-2xl animate-pulse-slow" />
                <div
                  className="absolute bottom-0 right-1/4 w-32 h-32 bg-secondary/5 rounded-full blur-2xl animate-pulse-slow"
                  style={{ animationDelay: '1s' }}
                />
              </div>

              <div className="relative flex flex-col items-center gap-4">
                <div
                  className={`
                    w-16 h-16 rounded-full flex items-center justify-center
                    transition-all duration-500
                    ${
                      isDragOver
                        ? 'bg-primary scale-110 shadow-2xl shadow-primary/50'
                        : 'bg-gradient-to-br from-primary/20 to-secondary/20 shadow-lg'
                    }
                  `}
                >
                  {isExtracting ? (
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                  ) : (
                    <Upload
                      className={`w-8 h-8 transition-all duration-300 ${
                        isDragOver ? 'text-white scale-110' : 'text-primary'
                      }`}
                    />
                  )}
                </div>

                <div>
                  <div className="text-lg font-bold text-foreground mb-2">
                    {isExtracting
                      ? '正在提取文本...'
                      : isDragOver
                        ? '松开以上传文件'
                        : '点击或拖拽文件到此处'}
                  </div>
                  <div className="text-sm text-muted-foreground mb-3">
                    支持 .docx 格式的Word文档
                  </div>

                  {uploadedFileName && (
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30">
                      <Check className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium text-green-500">
                        {uploadedFileName}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".docx,.doc"
              className="hidden"
              onChange={handleFileSelect}
            />

            {/* 提示信息 */}
            <div className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>文件大小建议不超过 10MB，支持拖拽上传</span>
            </div>
          </div>

          {/* 错误提示 */}
          {error && (
            <Alert
              variant="destructive"
              className="mb-8 animate-fade-in border-2"
            >
              <AlertCircle className="h-5 w-5" />
              <AlertDescription className="ml-2">{error}</AlertDescription>
            </Alert>
          )}

          {/* 提取结果区域 */}
          {extractedText && (
            <div className="p-6 rounded-3xl bg-gradient-to-br from-card/95 to-card/80 backdrop-blur-2xl border-2 border-border/50 shadow-2xl animate-fade-in">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <h3 className="text-lg font-semibold text-foreground">
                    提取结果
                  </h3>
                  <span className="text-xs text-muted-foreground">
                    ({extractedText.length} 字符)
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleCopy}
                    variant="outline"
                    size="sm"
                    className="rounded-xl border-2 hover:border-primary hover:bg-primary/5 transition-all duration-300"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 mr-1 text-green-500" />
                        <span className="text-green-500">已复制</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        复制
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleDownload}
                    variant="outline"
                    size="sm"
                    className="rounded-xl border-2 hover:border-primary hover:bg-primary/5 transition-all duration-300"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    下载
                  </Button>
                </div>
              </div>

              <div className="relative">
                <textarea
                  ref={textAreaRef}
                  value={extractedText}
                  readOnly
                  className="w-full min-h-[400px] max-h-[600px] p-4 bg-background/50 border-2 border-border rounded-xl text-sm leading-relaxed resize-y focus:outline-none focus:border-primary transition-all duration-300 font-mono"
                  style={{ scrollbarWidth: 'thin' }}
                />
                {/* 渐变遮罩效果 */}
                <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background/80 to-transparent pointer-events-none rounded-b-xl" />
              </div>
            </div>
          )}

          {/* 空状态提示 */}
          {!extractedText && !error && !isExtracting && (
            <div className="p-12 rounded-3xl bg-gradient-to-br from-card/50 to-card/30 backdrop-blur-xl border-2 border-dashed border-border/50 text-center animate-fade-in">
              <div className="relative inline-block mb-4">
                <div className="absolute inset-0 bg-primary/10 rounded-full blur-2xl animate-pulse-slow" />
                <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                  <FileText className="w-10 h-10 text-primary/60" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-muted-foreground mb-2">
                准备就绪
              </h3>
              <p className="text-sm text-muted-foreground/80">
                输入Word文档URL，点击"提取文本"开始
              </p>
            </div>
          )}
        </div>
      </main>

      <style jsx global>{`
        @keyframes pulse-slow {
          0%,
          100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 0.6;
            transform: scale(1.05);
          }
        }

        @keyframes spin-slow {
          from {
            transform: translate(-50%, -50%) rotate(0deg);
          }
          to {
            transform: translate(-50%, -50%) rotate(360deg);
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }

        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }

        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }

        /* 自定义滚动条样式 */
        textarea::-webkit-scrollbar {
          width: 8px;
        }

        textarea::-webkit-scrollbar-track {
          background: transparent;
        }

        textarea::-webkit-scrollbar-thumb {
          background: hsl(var(--primary) / 0.3);
          border-radius: 4px;
        }

        textarea::-webkit-scrollbar-thumb:hover {
          background: hsl(var(--primary) / 0.5);
        }
      `}</style>
    </>
  )
}
